import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createTruckAdmin } from '../../store/trucks';
import { useLoadAppData } from '../../hooks/useLoadAppData';

const TRUCK_TYPES = {
  'daycab_1_axle': 'Daycab 1 Axle',
  'daycab_2_axle': 'Daycab 2 Axle',
  'sleeper_1_axle': 'Sleeper 1 Axle',
  'sleeper_2_axle': 'Sleeper 2 Axle',
  'boxtruck': 'Boxtruck'
};

const statesList = [
  { name: "Alabama", abbreviation: "AL" },
  { name: "Alaska", abbreviation: "AK" },
  { name: "Arizona", abbreviation: "AZ" },
  { name: "Arkansas", abbreviation: "AR" },
  { name: "California", abbreviation: "CA" },
  { name: "Colorado", abbreviation: "CO" },
  { name: "Connecticut", abbreviation: "CT" },
  { name: "Delaware", abbreviation: "DE" },
  { name: "Florida", abbreviation: "FL" },
  { name: "Georgia", abbreviation: "GA" },
  { name: "Hawaii", abbreviation: "HI" },
  { name: "Idaho", abbreviation: "ID" },
  { name: "Illinois", abbreviation: "IL" },
  { name: "Indiana", abbreviation: "IN" },
  { name: "Iowa", abbreviation: "IA" },
  { name: "Kansas", abbreviation: "KS" },
  { name: "Kentucky", abbreviation: "KY" },
  { name: "Louisiana", abbreviation: "LA" },
  { name: "Maine", abbreviation: "ME" },
  { name: "Maryland", abbreviation: "MD" },
  { name: "Massachusetts", abbreviation: "MA" },
  { name: "Michigan", abbreviation: "MI" },
  { name: "Minnesota", abbreviation: "MN" },
  { name: "Mississippi", abbreviation: "MS" },
  { name: "Missouri", abbreviation: "MO" },
  { name: "Montana", abbreviation: "MT" },
  { name: "Nebraska", abbreviation: "NE" },
  { name: "Nevada", abbreviation: "NV" },
  { name: "New Hampshire", abbreviation: "NH" },
  { name: "New Jersey", abbreviation: "NJ" },
  { name: "New Mexico", abbreviation: "NM" },
  { name: "New York", abbreviation: "NY" },
  { name: "North Carolina", abbreviation: "NC" },
  { name: "North Dakota", abbreviation: "ND" },
  { name: "Ohio", abbreviation: "OH" },
  { name: "Oklahoma", abbreviation: "OK" },
  { name: "Oregon", abbreviation: "OR" },
  { name: "Pennsylvania", abbreviation: "PA" },
  { name: "Rhode Island", abbreviation: "RI" },
  { name: "South Carolina", abbreviation: "SC" },
  { name: "South Dakota", abbreviation: "SD" },
  { name: "Tennessee", abbreviation: "TN" },
  { name: "Texas", abbreviation: "TX" },
  { name: "Utah", abbreviation: "UT" },
  { name: "Vermont", abbreviation: "VT" },
  { name: "Virginia", abbreviation: "VA" },
  { name: "Washington", abbreviation: "WA" },
  { name: "West Virginia", abbreviation: "WV" },
  { name: "Wisconsin", abbreviation: "WI" },
  { name: "Wyoming", abbreviation: "WY" },
  { name: "District of Columbia", abbreviation: "DC" },
];

function AddTruckPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoading = useLoadAppData();
  const sessionUser = useSelector(state => state.session.user);
  const accounts = useSelector(state => Object.values(state.accounts));
  const locations = useSelector(state => state.locations || {});
  const buildings = useSelector(state => state.buildings || []);
  const yards = useSelector(state => state.yards || []);
  const dropdownRef = useRef(null);
  const locationDropdownRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [locationActiveIndex, setLocationActiveIndex] = useState(-1);
  const [stateSearchTerm, setStateSearchTerm] = useState('');
  const [typeSearchTerm, setTypeSearchTerm] = useState('');
  const [locationSearchTerm, setLocationSearchTerm] = useState('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const typeDropdownRef = useRef(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);

  const [formData, setFormData] = useState({
    truck_number: '',
    truck_location: '',
    license_plate: '',
    license_state: '',
    type: '',
    year: '',
    make: '',
    model: '',
    red_tagged: false,
    yellow_tagged: false,
    in_shop: false,
    last_inspection: new Date().toISOString().split('T')[0],
    next_inspection: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    last_maintenance: new Date().toISOString().split('T')[0],
    next_maintenance: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    tagged_description: '',
    account_id: sessionUser?.is_admin ? '' : sessionUser?.account_id
  });

  const [errors, setErrors] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [filteredStates, setFilteredStates] = useState(statesList);

  // Get all available locations
  useEffect(() => {
    const standardLocations = ['Road', 'Shop'];
    const allLocations = [...standardLocations];

    // Add buildings with door numbers
    buildings
      .filter(building => building.account_id === (formData.account_id || sessionUser.account_id))
      .forEach(building => {
        // Add doors for each building
        for (let i = 1; i <= building.building_door_number; i++) {
          allLocations.push(`${building.building_name}-${i}`);
        }
      });

    // Add yards
    yards
      .filter(yard => yard.account_id === (formData.account_id || sessionUser.account_id))
      .forEach(yard => {
        allLocations.push(yard.name);
      });

    setAvailableLocations(allLocations.sort());
    setFilteredLocations(allLocations.sort());
  }, [formData.account_id, buildings, yards, sessionUser.account_id]);

  // Handle click outside to close dropdowns
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target)) {
        setShowTypeDropdown(false);
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
        setShowLocationDropdown(false);
      }
    }

    // Add event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Remove event listener on cleanup
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef, typeDropdownRef, locationDropdownRef]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown) return;

    // Arrow down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(prevIndex =>
        prevIndex < filteredStates.length - 1 ? prevIndex + 1 : prevIndex
      );
    }

    // Arrow up
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(prevIndex => prevIndex > 0 ? prevIndex - 1 : 0);
    }

    // Enter key
    else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleStateSelect(filteredStates[activeIndex].abbreviation);
    }

    // Escape key
    else if (e.key === 'Escape') {
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  // Reset active index when filtered states change
  useEffect(() => {
    setActiveIndex(-1);
  }, [filteredStates]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle checkbox inputs
    if (type === 'checkbox') {
      setFormData(prev => ({ ...prev, [name]: checked }));
      return;
    }

    // Define fields that should be uppercase
    const uppercaseFields = ['license_plate', 'license_state', 'truck_location', 'truck_number'];

    // Handle different input fields
    const processValue = {
      'year': () => {
        if (!/^\d*$/.test(value)) return formData.year;
        const yearNum = parseInt(value);
        if (yearNum > new Date().getFullYear()) return formData.year;
        return value.slice(0, 4);
      },
      'default': () => uppercaseFields.includes(name) ? value.toUpperCase() : value
    };

    const newValue = (processValue[name] || processValue.default)();

    setFormData(prev => ({ ...prev, [name]: newValue }));
  };

  const handleStateInputChange = (e) => {
    const inputValue = e.target.value.toUpperCase();
    setStateSearchTerm(inputValue);
    setFormData({ ...formData, license_state: inputValue });

    if (inputValue) {
      // Filter states by name or abbreviation
      const filtered = statesList
        .filter(
          (state) =>
            state.name.toLowerCase().includes(inputValue.toLowerCase()) ||
            state.abbreviation.includes(inputValue)
        );
      setFilteredStates(filtered);
      setShowDropdown(true);
    } else {
      setFilteredStates(statesList);
      setShowDropdown(inputValue === '');
    }
  };

  const handleTypeInputChange = (e) => {
    const inputValue = e.target.value;
    setTypeSearchTerm(inputValue);

    if (inputValue) {
      setShowTypeDropdown(true);
    } else {
      setShowTypeDropdown(false);
    }
  };

  const handleStateSelect = (abbreviation) => {
    setFormData({ ...formData, license_state: abbreviation });
    setShowDropdown(false);
  };

  // Filter truck types based on search term
  const filteredTypes = typeSearchTerm
    ? Object.entries(TRUCK_TYPES).filter(([key, value]) =>
        value.toLowerCase().includes(typeSearchTerm.toLowerCase()) ||
        key.toLowerCase().includes(typeSearchTerm.toLowerCase())
      )
    : Object.entries(TRUCK_TYPES);

  // Function to handle type selection
  const handleTypeSelect = (type) => {
    setFormData(prev => ({ ...prev, type }));
    setShowTypeDropdown(false);
    setTypeSearchTerm('');
  };

  const handleStatusChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked,
      // If in_shop is selected, automatically set location to "Shop"
      truck_location: name === "in_shop" && checked ? "Shop" : prev.truck_location
    }));
  };

  // Handle keyboard navigation for location dropdown
  const handleLocationKeyDown = (e) => {
    if (!showLocationDropdown) return;

    // Arrow down
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setLocationActiveIndex(prevIndex =>
        prevIndex < filteredLocations.length - 1 ? prevIndex + 1 : prevIndex
      );
    }

    // Arrow up
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setLocationActiveIndex(prevIndex => prevIndex > 0 ? prevIndex - 1 : 0);
    }

    // Enter key
    else if (e.key === 'Enter' && locationActiveIndex >= 0) {
      e.preventDefault();
      handleLocationSelect(filteredLocations[locationActiveIndex]);
    }

    // Escape key
    else if (e.key === 'Escape') {
      setShowLocationDropdown(false);
      setLocationActiveIndex(-1);
    }
  };

  // Reset active index when filtered locations change
  useEffect(() => {
    setLocationActiveIndex(-1);
  }, [filteredLocations]);

  const handleLocationInputChange = (e) => {
    const inputValue = e.target.value.toUpperCase();
    setLocationSearchTerm(inputValue);
    setFormData({ ...formData, truck_location: inputValue });

    if (inputValue) {
      // Filter locations
      const filtered = availableLocations
        .filter(location => location.toUpperCase().includes(inputValue));
      setFilteredLocations(filtered);
      setShowLocationDropdown(true);
    } else {
      setFilteredLocations(availableLocations);
      setShowLocationDropdown(inputValue === '');
    }
  };

  const handleLocationSelect = (location) => {
    setFormData({ ...formData, truck_location: location });
    setShowLocationDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = [];

    // Basic Information validations
    if (!formData.truck_number) validationErrors.push('Truck number is required');
    if (!formData.truck_location) validationErrors.push('Truck location is required');
    if (!formData.license_plate) validationErrors.push('License plate is required');
    if (!formData.license_state) validationErrors.push('License state is required');
    if (formData.license_state.length !== 2) validationErrors.push('License state must be 2 characters');
    if (sessionUser?.is_admin && !formData.account_id) validationErrors.push('Account is required');

    // Vehicle Details validations
    if (!formData.type) validationErrors.push('Truck type is required');
    if (!formData.year) validationErrors.push('Year is required');
    if (formData.year && (formData.year < 1900 || formData.year > new Date().getFullYear() + 1)) {
      validationErrors.push('Year must be between 1900 and next year');
    }
    if (!formData.make) validationErrors.push('Make is required');
    if (!formData.model) validationErrors.push('Model is required');

    // Tagged Description validation
    if ((formData.red_tagged || formData.yellow_tagged) && !formData.tagged_description) {
      validationErrors.push('Tagged description is required when truck is tagged');
    }

    // Date validations
    const today = new Date().toISOString().split('T')[0];
    if (formData.last_inspection > today) {
      validationErrors.push('Last inspection date cannot be in the future');
    }
    if (formData.last_maintenance > today) {
      validationErrors.push('Last maintenance date cannot be in the future');
    }
    if (formData.next_inspection < today) {
      validationErrors.push('Next inspection date must be in the future');
    }
    if (formData.next_maintenance < today) {
      validationErrors.push('Next maintenance date must be in the future');
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      const result = await dispatch(createTruckAdmin(formData));
      if (result) {
        history.push('/');
      }
    } catch (error) {
      setErrors([error.toString()]);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          <div className="ml-4 text-2xl text-blue-600">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="text-8xl font-bold mb-5 text-center text-blue-600">Add New Truck</h1>

        {errors.length > 0 && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            <ul className="list-disc list-inside">
              {errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white shadow-lg rounded-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Left Column - Basic Info */}
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Basic Information</h2>

              <div className="space-y-4">
                <div>
                  <label htmlFor="truck-number" className="block text-2xl font-medium text-gray-700 mb-2">
                    Truck Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="truck-number"
                    type="text"
                    name="truck_number"
                    value={formData.truck_number}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex flex-col relative" ref={locationDropdownRef}>
                  <label htmlFor="truck-location" className="block text-2xl font-medium text-gray-700 mb-2">
                    Location <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="truck-location"
                      type="text"
                      name="truck_location"
                      value={formData.truck_location}
                      onChange={handleLocationInputChange}
                      onKeyDown={handleLocationKeyDown}
                      className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                      placeholder="Enter location..."
                      onFocus={() => setShowLocationDropdown(true)}
                      required
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </div>

                  {showLocationDropdown && (
                    <ul className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto z-50 bg-white shadow-lg rounded-md border border-gray-300">
                      {filteredLocations.length === 0 ? (
                        <li className="p-3 text-gray-500 text-center">
                          No results found
                        </li>
                      ) : (
                        filteredLocations.map((location, index) => (
                          <li
                            key={location}
                            onClick={() => handleLocationSelect(location)}
                            className={`p-3 cursor-pointer text-xl border-b border-gray-200 ${
                              index === locationActiveIndex ? 'bg-blue-100' : 'hover:bg-blue-50'
                            } ${formData.truck_location === location ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                          >
                            <span>{location}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>

                <div>
                  <label htmlFor="license-plate" className="block text-2xl font-medium text-gray-700 mb-2">
                    License Plate <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="license-plate"
                    type="text"
                    name="license_plate"
                    value={formData.license_plate}
                    onChange={handleChange}
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    style={{ textTransform: 'uppercase' }}
                  />
                </div>

                <div className="flex flex-col relative" ref={dropdownRef}>
                  <label htmlFor="license-state" className="text-2xl font-medium text-gray-700 mb-2">
                    License State <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="license-state"
                      type="text"
                      name="license_state"
                      value={formData.license_state}
                      onChange={handleStateInputChange}
                      onKeyDown={handleKeyDown}
                      className="w-full p-3 text-xl border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                      placeholder="Type state name or abbreviation..."
                      onFocus={() => setShowDropdown(true)}
                      required
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </div>

                  {showDropdown && (
                    <ul className="absolute top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto z-50 bg-white shadow-lg rounded-md border border-gray-300">
                      {filteredStates.length === 0 ? (
                        <li className="p-3 text-gray-500 text-center">
                          No results found
                        </li>
                      ) : (
                        filteredStates.map((state, index) => (
                          <li
                            key={state.abbreviation}
                            onClick={() => handleStateSelect(state.abbreviation)}
                            className={`p-3 cursor-pointer text-xl border-b border-gray-200 flex justify-between ${
                              index === activeIndex ? 'bg-blue-100' : 'hover:bg-blue-50'
                            } ${formData.license_state === state.abbreviation ? 'bg-blue-50 border-l-4 border-blue-500' : ''}`}
                          >
                            <span>{state.name}</span>
                            <span className="font-bold">{state.abbreviation}</span>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>

                {sessionUser?.is_admin && (
                  <div>
                    <label htmlFor="account-select" className="block text-2xl font-medium text-gray-700 mb-2">
                      Account <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="account-select"
                      name="account_id"
                      value={formData.account_id}
                      onChange={handleChange}
                      required
                      className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Select Account</option>
                      {accounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {account.account_name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Vehicle Details */}
            <div className="space-y-6">
              <h2 className="text-4xl font-semibold mb-6 text-gray-800">Vehicle Details</h2>

              <div className="space-y-4">
                <div className="relative" ref={typeDropdownRef}>
                  <label htmlFor="vehicle-type" className="block text-2xl font-medium text-gray-700 mb-2">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      id="vehicle-type"
                      type="text"
                      placeholder="Search for a truck type..."
                      value={showTypeDropdown ? typeSearchTerm : (formData.type ? TRUCK_TYPES[formData.type] || formData.type : '')}
                      onChange={handleTypeInputChange}
                      onClick={() => setShowTypeDropdown(true)}
                      className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                      required
                    />
                    {/* Dropdown arrow indicator */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    </div>
                  </div>

                  {/* Dropdown */}
                  {showTypeDropdown && (
                    <div className="absolute z-10 w-full mt-0.5 top-full left-0 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredTypes.length === 0 ? (
                        <div className="p-3 text-center text-gray-500">No truck types found</div>
                      ) : (
                        filteredTypes.map(([key, value]) => (
                          <div
                            key={key}
                            className={`p-3 cursor-pointer hover:bg-blue-100 focus:bg-blue-100 ${
                              formData.type === key ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                            }`}
                            onClick={() => handleTypeSelect(key)}
                            tabIndex="0"
                            role="option"
                            aria-selected={formData.type === key}
                          >
                            {value}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label htmlFor="truck-year" className="block text-2xl font-medium text-gray-700 mb-2">
                    Year <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="truck-year"
                    type="text"
                    name="year"
                    value={formData.year}
                    onChange={handleChange}
                    min="1900"
                    max={new Date().getFullYear()}
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="truck-make" className="block text-2xl font-medium text-gray-700 mb-2">
                    Make <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="truck-make"
                    type="text"
                    name="make"
                    value={formData.make}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label htmlFor="truck-model" className="block text-2xl font-medium text-gray-700 mb-2">
                    Model <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="truck-model"
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    required
                    className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h2 className="text-4xl font-semibold mb-6 text-gray-800">Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <label htmlFor="red-tagged" className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="red-tagged"
                  name="red_tagged"
                  checked={formData.red_tagged}
                  onChange={handleStatusChange}
                  className="h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <span className="text-2xl text-gray-700">Red Tagged</span>
              </label>

              <label htmlFor="yellow-tagged" className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="yellow-tagged"
                  name="yellow_tagged"
                  checked={formData.yellow_tagged}
                  onChange={handleStatusChange}
                  className="h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <span className="text-2xl text-gray-700">Yellow Tagged</span>
              </label>

              <label htmlFor="in-shop" className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="in-shop"
                  name="in_shop"
                  checked={formData.in_shop}
                  onChange={handleStatusChange}
                  className="h-5 w-5 text-blue-600 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
                <span className="text-2xl text-gray-700">Shop</span>
              </label>
            </div>

            <div className="mt-6">
              <label htmlFor="tagged-description" className="block text-2xl font-medium text-gray-700 mb-2">
                Tagged Description
              </label>
              <textarea
                id="tagged-description"
                name="tagged_description"
                value={formData.tagged_description}
                onChange={handleChange}
                rows="4"
                className="w-full p-3 border border-gray-300 rounded-lg text-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
              />
            </div>
          </div>

          {/* Bottom Buttons */}
          <div className="flex justify-end gap-4 mt-8 pt-6 border-t">
            <button
              type="button"
              onClick={() => history.goBack()}
              className="px-6 py-3 text-2xl text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-all duration-200 w-1/4 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              Cancel
            </button>
            <button id="add-truck-button"
              type="submit"
              className="px-6 py-3 text-2xl text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 w-1/4 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            >
              Add Truck
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddTruckPage;
